import { useId } from 'react';
import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface SegmentedProps<T extends string> {
  value: T;
  options: ReadonlyArray<SegmentedOption<T>>;
  onChange: (value: T) => void;
  'aria-label'?: string;
}

/**
 * Built on radio inputs rather than buttons so arrow keys move between options
 * and screen readers announce "1 of 3" — the CSS hides the native control and
 * styles the label via `:has(input:checked)`.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
}: SegmentedProps<T>) {
  const name = useId();

  return (
    <div className="seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => (
        <label key={option.value} className="seg-opt">
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}
